<template>
   <div class="bg-white q-pa-md">
      <div id="main-container">
         <h1 class="text-left text-h4">Configure Page View Reward</h1>
         <p id="intro">
            Only use a Page View Reward if a specific page—like a "Thank You" or
            order confirmation page—means a conversion happened. Learn more
            about
            <a
               href="https://docs.ezbot.ai/get-started/strategize/"
               target="_blank"
               >how to pick the right reward signal here</a
            >
            and
            <a
               href="https://docs.ezbot.ai/get-started/strategize/"
               target="_blank"
               >how to setup rewards here.
            </a>
         </p>
         <QForm id="rewards-edit-form" @submit="openSaveDialog">
            <h3 class="text-h5">Page View Events</h3>
            <QInput
               v-model="urlPattern"
               label="URL Pattern"
               hint="Page views matching this regular expression will count as rewards."
               :rules="[isValidRegex]"
            />
            <div id="submit-container">
               <QBtn
                  id="submit"
                  type="submit"
                  color="primary"
                  class="action-button"
                  :disable="showDisabledSubmit"
                  >Save</QBtn
               >
            </div>
         </QForm>
         <p id="after-form">
            <a
               href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_expressions"
               target="_blank"
            >
               Learn more about regular expressions (regex)
            </a>
         </p>
      </div>
   </div>
</template>
<script setup lang="ts">
import { QForm, useQuasar } from "quasar"
import { ref, computed, ComputedRef, onMounted, nextTick } from "vue"
import { useProjectStore } from "../stores/projectStore"
import { toast } from "vue3-toastify"
import { PageViewReward } from "../types/projectSettings"
import { callWithLoading } from "@utils/callWithLoading"
import { rewardsTour } from "../tours/rewardsTour"
import { useTour } from "../composables/useTour"

const $q = useQuasar()
const projectStore = useProjectStore()
const localProjectSettings = await callWithLoading(
   projectStore.getProjectSettings,
   $q,
   projectStore.projectId!,
)
const urlPattern = ref("")
if (localProjectSettings.settings.rewards?.pageViews) {
   urlPattern.value =
      localProjectSettings.settings.rewards.pageViews[0].urlPattern
} else {
   urlPattern.value = "https:\/\/example.com\/checkouts\/*"
}

const isValidRegex = (pattern: string) => {
   try {
      new RegExp(pattern)
      return true
   } catch (_e) {
      return "Must use valid Regex pattern"
   }
}

const pageViewRewards: ComputedRef<PageViewReward[]> = computed(() => {
   return [
      {
         type: "pageViewReward",
         urlPattern: urlPattern.value,
      } as PageViewReward,
   ]
})

const openSaveDialog = () => {
   $q.dialog({
      title: "Save Rewards",
      message:
         "Are you sure you want to save these rewards to your project settings?",
      cancel: true,
   }).onOk(async () => {
      await callWithLoading(projectStore.updateProjectSettings, $q, {
         settings: {
            holdbackPercentage:
               localProjectSettings.settings.holdbackPercentage,
            optimizationMetric:
               localProjectSettings.settings.optimizationMetric,
            rewards: {
               pageViews: pageViewRewards.value,
            },
         },
      })
      toast.success("Rewards saved")
   })
}

const showDisabledSubmit = computed((): boolean => {
   const urlPatternprojectSettings =
      localProjectSettings.settings.rewards?.pageViews
   return (
      urlPatternprojectSettings != undefined &&
      urlPatternprojectSettings === pageViewRewards.value
   )
})

// Initialize the tour
const { autoStartTour } = useTour(rewardsTour)

// Start the tour after data is loaded and view is rendered
onMounted(async () => {
   await nextTick() // wait for Vue to re-render
   if (localProjectSettings) {
      await autoStartTour() // if data is present, start the tour
   }
})
</script>
<style lang="scss" scoped>
#main-container {
   width: 100%;
   max-width: 600px;
}
h3 {
   margin-bottom: 0.5em;
   text-align: left;
}
#rewards-edit-form {
   border: 1px dotted darkgrey;
   padding: 1em;
   margin-bottom: 1em;
   width: 100%;
}
.q-field {
   margin-bottom: 10px;
}
#after-form {
   width: 100%;
   font-style: italic;
   text-align: left;
   a {
      color: $grey-9;
   }
}
#intro {
   text-align: left;
   width: 100%;
   span {
      font-weight: bold;
   }
}
#submit-container {
   margin-top: 2em;
   width: 100%;
   text-align: right;
}
.text-left {
   text-align: left;
}
</style>
